import { Routes } from '@angular/router';
import { requireRole, SUPER_ADMIN_ROLE } from '@sisoma/auth';

import { Shell } from './core/layout/shell';

/**
 * All feature pages are lazy-loaded children of the shell, so the sidenav and
 * main-area frame persist across navigation.
 *
 * Adding a section = add a child route here + an entry in
 * `core/navigation/nav-items.ts`.
 */
export const routes: Routes = [
  // The 403 page must sit OUTSIDE the role-guarded shell and be matched BEFORE
  // it — otherwise the guard's redirect here would re-trigger the guard (or
  // fall through the shell's `**`) and loop. Rendered standalone, without the
  // sidenav frame.
  {
    path: 'forbidden',
    title: 'Not authorised',
    loadComponent: () =>
      import('./features/not-authorised/not-authorised').then((m) => m.NotAuthorised),
  },
  {
    path: '',
    component: Shell,
    // Coarse gate for the whole surface (fp1-04): logged-out → shared login;
    // logged-in without the superAdmin role → `/forbidden`. Admin is role-gated
    // rather than capability-gated, so there are no per-feature
    // `requireCapabilities(...)` guards on the child routes.
    canActivate: [requireRole(SUPER_ADMIN_ROLE)],
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'dashboard' },
      {
        path: 'dashboard',
        title: 'Dashboard',
        loadComponent: () => import('./features/dashboard/dashboard').then((m) => m.Dashboard),
      },
      {
        path: 'settings',
        title: 'Settings',
        data: { pageTitle: 'Settings' },
        loadComponent: () =>
          import('./features/placeholder/placeholder-page').then((m) => m.PlaceholderPage),
      },
      {
        path: 'partner-areas',
        title: 'Partner areas',
        loadComponent: () =>
          import('./features/partner-areas/partner-areas').then((m) => m.PartnerAreas),
      },
      // One area: compliance switches, members, deactivate. A child path of the
      // list, so the sidebar's Partner areas entry stays lit on it. `id` binds via
      // `withComponentInputBinding`.
      {
        path: 'partner-areas/:id',
        title: 'Partner area',
        loadComponent: () =>
          import('./features/partner-areas/partner-area-detail/partner-area-detail').then(
            (m) => m.PartnerAreaDetailPage,
          ),
      },
      // The platform-owned compliance area: the artefacts the platform
      // maintains for every partner — FCA baseline documents, policy packs,
      // compliance testing. Firm-owned, capability-gated compliance (a firm's
      // bot, triage rules, approvals, access, reports) is NOT here; it lives in
      // `projects/partner-area` under `features/compliance/`.
      //
      // `compliance` renders the area's own landing page rather than
      // redirecting into a child. FCA documents is real (Waves 1–2 of the
      // FCA-UPD re-home: versions, ingest, and the Gate 1 and Gate 2 reviews
      // under it); policy packs and compliance testing are still placeholders —
      // routes and sidebar entries wired, real UI to come (the rest of the FCA
      // re-home for packs, GOV-11c for testing).
      {
        path: 'compliance',
        title: 'Compliance',
        loadComponent: () =>
          import('./features/compliance/dashboard/compliance-dashboard').then(
            (m) => m.ComplianceDashboard,
          ),
      },
      {
        path: 'compliance/fca-documents',
        title: 'FCA documents',
        loadComponent: () =>
          import('./features/compliance/fca-documents/fca-documents').then(
            (m) => m.ComplianceFcaDocuments,
          ),
      },
      // Gate 1 for one ingestion run. A child path rather than a sibling, so the
      // breadcrumb and the back link both say where it came from.
      {
        path: 'compliance/fca-documents/runs/:runId',
        title: 'Gate 1 review',
        loadComponent: () =>
          import('./features/compliance/fca-documents/fca-run-review').then((m) => m.FcaRunReview),
      },
      // Gate 2 for the same run. A child of the Gate 1 path rather than a
      // sibling, because it is the next step ON that run and both screens link
      // to each other.
      {
        path: 'compliance/fca-documents/runs/:runId/diff',
        title: 'Gate 2 review',
        loadComponent: () =>
          import('./features/compliance/fca-documents/fca-run-diff').then((m) => m.FcaRunDiff),
      },
      // Publish for the same run: the step after Gate 2, reached from Gate 2's
      // completion banner. A child of the run path for the same reason Gate 2
      // is — it is the next step ON that run.
      {
        path: 'compliance/fca-documents/runs/:runId/publish',
        title: 'Publish version',
        loadComponent: () =>
          import('./features/compliance/fca-documents/fca-run-publish').then(
            (m) => m.FcaRunPublish,
          ),
      },
      {
        path: 'compliance/policy-packs',
        title: 'Policy packs',
        loadComponent: () =>
          import('./features/compliance/policy-packs/policy-packs').then(
            (m) => m.CompliancePolicyPacks,
          ),
      },
      {
        path: 'compliance/testing',
        title: 'Compliance testing',
        loadComponent: () =>
          import('./features/compliance/testing/compliance-testing').then(
            (m) => m.ComplianceTesting,
          ),
      },
      // SSO config is per-partner (keyed by `:creatorId`). The bare path lands on
      // a manual-entry field that navigates to the id route; a drill-in link
      // from the partner-areas list arrives with fp1-11. Both bind `creatorId`
      // via `withComponentInputBinding`.
      {
        path: 'sso-config',
        title: 'SSO configuration',
        loadComponent: () => import('./features/sso-config/sso-config').then((m) => m.SsoConfig),
      },
      {
        path: 'sso-config/:creatorId',
        title: 'SSO configuration',
        loadComponent: () => import('./features/sso-config/sso-config').then((m) => m.SsoConfig),
      },
      {
        path: '**',
        title: 'Page not found',
        loadComponent: () => import('./features/not-found/not-found').then((m) => m.NotFound),
      },
    ],
  },
];
